import React from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Container,
    Typography,
    Paper,
    IconButton,
    Link,
    Chip,
    Stack,
    Skeleton,
    Alert,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    OpenInNew as OpenInNewIcon,
    YouTube as YouTubeIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import { ROUTES_NAMES } from '../components/Layout';
import { useBrandExplorerStore } from '../stores/brandExplorerStore';
import { useTrackerStore } from '../stores/trackerStore';
import { useAuth } from '../contexts/AuthContext';

interface BrandExplorerContentDetailProps {
    fixedBrandId?: string;
}

const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
};

const BrandExplorerContentDetail: React.FC<BrandExplorerContentDetailProps> = ({ fixedBrandId }) => {
    const { contentId } = useParams<{ contentId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { selectedBrand } = useBrandExplorerStore();
    const { startDate: trackerStartDate, endDate: trackerEndDate } = useTrackerStore();
    const { getCompanyTrackerBrandId } = useAuth();

    const isTracker = location.pathname.startsWith('/tracker');
    const isEnglish = !!fixedBrandId;

    const brandIdParam = searchParams.get('brandId');
    const yearParam = searchParams.get('year');
    const startDateParam = isTracker ? trackerStartDate : (searchParams.get('startDate') || undefined);
    const endDateParam = isTracker ? trackerEndDate : (searchParams.get('endDate') || undefined);
    const yearFromUrl = yearParam ? parseInt(yearParam) : undefined;

    const brandId = fixedBrandId || (isTracker ? getCompanyTrackerBrandId() : null) || selectedBrand?._id || brandIdParam || null;
    const year = fixedBrandId ? 2026 : isTracker ? undefined : yearFromUrl;

    const state = location.state as { content?: any; mentions?: any[] } | undefined;
    const hasState = state?.content && state.content._id;

    const { data: fetchedData, isLoading: fetchLoading, error: fetchError } = useQuery({
        queryKey: ['explorer-content-mentions', contentId],
        queryFn: () => apiService.getExplorerContentMentions(contentId!),
        enabled: !!contentId && !hasState,
        staleTime: 10 * 60 * 1000,
    });

    const content = hasState ? state.content : fetchedData?.content;
    const mentions = hasState ? (state.mentions || []) : (fetchedData?.mentions || []);

    const handleBack = () => {
        const baseRoute = fixedBrandId
            ? ROUTES_NAMES.TONYBET_EXPLORER_YEAR
            : isTracker
                ? ROUTES_NAMES.TRACKER
                : ROUTES_NAMES.TONYBET_EXPLORER_YEAR;
        if (fixedBrandId || isTracker) {
            navigate(baseRoute);
        } else {
            const params = new URLSearchParams();
            if (brandId) params.set('brandId', brandId);
            if (year !== undefined) params.set('year', year.toString());
            if (startDateParam) params.set('startDate', startDateParam);
            if (endDateParam) params.set('endDate', endDateParam);
            const queryString = params.toString();
            navigate(queryString ? `${baseRoute}?${queryString}` : baseRoute);
        }
    };

    if (!hasState && fetchLoading) {
        return (
            <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Skeleton variant="text" width={200} height={36} />
                </Stack>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1, mb: 2 }} />
                <Skeleton variant="text" width="100%" height={24} />
                <Skeleton variant="text" width="60%" height={24} />
            </Container>
        );
    }

    if (!hasState && (fetchError || !content)) {
        return (
            <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                <IconButton onClick={handleBack} sx={{ mb: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Alert severity="error">
                    {isEnglish ? 'Content not found.' : 'Contenido no encontrado.'}
                </Alert>
            </Container>
        );
    }

    const title = content?.title || (isEnglish ? 'Untitled' : 'Sin título');
    const views = content?.views ?? content?.insights?.views ?? 0;
    const publishedAt = content?.publishedAt;
    const url = content?.url;
    const source = content?.source || 'youtube';

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <IconButton onClick={handleBack} sx={{ mr: -0.5 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" color="text.secondary">
                    {isTracker ? 'Tracker' : 'TonyBet'}
                    {year != null && ` · ${year}`}
                </Typography>
            </Box>

            <Paper variant="section">
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    {source === 'youtube' && (
                        <Box sx={{ color: 'text.secondary' }}>
                            <YouTubeIcon sx={{ fontSize: 40 }} />
                        </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
                            {title}
                        </Typography>
                        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 1 }}>
                            {views > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    {formatNumber(views)} {isEnglish ? 'views' : 'vistas'}
                                </Typography>
                            )}
                            {publishedAt && (
                                <Typography variant="body2" color="text.secondary">
                                    {new Date(publishedAt).toLocaleDateString(isEnglish ? 'en-US' : 'es-CL', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric',
                                    })}
                                </Typography>
                            )}
                        </Stack>
                        {url && (
                            <Link
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
                            >
                                {isEnglish ? 'Open' : 'Abrir'} <OpenInNewIcon fontSize="small" />
                            </Link>
                        )}
                    </Box>
                </Box>

                {Array.isArray(mentions) && mentions.length > 0 && (
                    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            {isEnglish ? 'Brands' : 'Marcas'}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {mentions.map((m: any) => (
                                <Chip
                                    key={m._id}
                                    label={m.brandName || m.brand?.name || '—'}
                                    size="small"
                                    variant="outlined"
                                />
                            ))}
                        </Stack>
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default BrandExplorerContentDetail;
